import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Calendar, Target, CheckCircle2, Clock, AlertCircle, Circle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const CONSULT_LABEL = {
    ambulatorial: "Ambulatorial",
    domiciliar: "Domiciliar",
    telemedicina_canabis: "Telemedicina Canábis",
};
const STATUS_LABEL = {
    scheduled: "Agendada",
    intake_pending: "Aguardando preenchimento",
    intake_completed: "Pré-consulta preenchida",
    in_progress: "Em andamento",
    completed: "Concluída",
    cancelled: "Cancelada",
};
const PRIORITY_META = {
    alta: { label: "Alta", cls: "bg-[#F0E0DC] text-[#7A2E1F] border-[#7A2E1F]" },
    media: { label: "Média", cls: "bg-[#FBF4E5] text-[#8A6A15] border-[#8A6A15]" },
    baixa: { label: "Baixa", cls: "bg-[#E5EFE0] text-[#315B3A] border-[#315B3A]" },
};

function formatDate(dt) {
    if (!dt) return "Sem data";
    try {
        const d = new Date(dt);
        if (isNaN(d.getTime())) return dt;
        return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return dt; }
}

function isPast(dt) {
    if (!dt) return false;
    try { return new Date(dt).getTime() < Date.now(); } catch { return false; }
}

export default function ReturnsAndGoalsTab({ patientId, currentConsultationId, canEdit }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingIdx, setSavingIdx] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/patients/${patientId}/follow-up`);
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [patientId]);

    const toggleGoal = async (g, idx, newVal) => {
        if (!canEdit) return;
        setSavingIdx(idx);
        try {
            await api.post(`/patients/${patientId}/toggle-goal`, {
                care_plan_id: g.care_plan_id,
                problem_index: g.problem_index,
                goal_index: g.goal_index,
                achieved: newVal,
            });
            // Optimistic update
            setData((d) => {
                const next = { ...d, goals: [...d.goals] };
                next.goals[idx] = { ...next.goals[idx], achieved: newVal, achieved_at: newVal ? new Date().toISOString() : null };
                return next;
            });
            toast.success(newVal ? "Meta marcada como atingida." : "Meta reaberta.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingIdx(null); }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand" size={28} /></div>;
    if (!data) return null;

    const consultations = data.consultations || [];
    const goals = data.goals || [];
    const achievedCount = goals.filter((g) => g.achieved).length;
    const pendingGoals = goals.filter((g) => !g.achieved);
    const achievedGoals = goals.filter((g) => g.achieved);

    return (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="returns-goals-tab">
            {/* Timeline of consultations */}
            <div className="lg:col-span-2 space-y-4">
                <div className="card-earthy">
                    <div className="flex items-center gap-2 mb-4 text-brand">
                        <Calendar size={18} /> <h2 className="font-display text-xl font-medium">Linha do tempo — Consultas de retorno</h2>
                    </div>
                    <p className="text-sm text-muted-clinical mb-4">
                        Ordenadas cronologicamente. Retornos futuros ficam destacados para acompanhamento.
                    </p>
                    {consultations.length === 0 ? (
                        <div className="text-sm text-muted-clinical py-6 text-center">
                            Nenhuma consulta cadastrada para este paciente.
                        </div>
                    ) : (
                        <ol className="relative border-l-2 border-[rgba(30,63,51,0.15)] pl-6 space-y-4">
                            {consultations.map((c, i) => {
                                const past = isPast(c.scheduled_at);
                                const isCurrent = c.id === currentConsultationId;
                                const isFuture = c.scheduled_at && !past;
                                const dotCls = isCurrent
                                    ? "bg-brand text-white"
                                    : past
                                    ? "bg-alt text-brand"
                                    : "bg-[#FBF4E5] text-[#8A6A15]";
                                const Icon = isCurrent ? CheckCircle2 : past ? CheckCircle2 : Clock;
                                return (
                                    <li key={c.id} data-testid={`consult-timeline-${c.id}`} className="relative">
                                        <span className={`absolute -left-[35px] w-6 h-6 rounded-full flex items-center justify-center border-2 border-white ${dotCls}`}>
                                            <Icon size={12} />
                                        </span>
                                        <div className={`rounded-xl p-3 border ${isCurrent ? "border-brand bg-alt" : "border-[rgba(30,63,51,0.08)]"}`}>
                                            <div className="flex items-start justify-between gap-2 flex-wrap">
                                                <div>
                                                    <div className="text-xs uppercase tracking-wider text-brand-secondary font-medium">
                                                        {formatDate(c.scheduled_at)}
                                                    </div>
                                                    <div className="text-sm font-medium text-brand mt-0.5">
                                                        {CONSULT_LABEL[c.consultation_type] || c.consultation_type}
                                                        {isCurrent && <span className="ml-2 text-[10px] bg-brand text-white px-1.5 py-0.5 rounded uppercase tracking-wider">Atual</span>}
                                                        {isFuture && !isCurrent && <span className="ml-2 text-[10px] bg-[#FBF4E5] text-[#8A6A15] px-1.5 py-0.5 rounded uppercase tracking-wider">Retorno futuro</span>}
                                                    </div>
                                                </div>
                                                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-alt text-brand-secondary font-medium">
                                                    {STATUS_LABEL[c.status] || c.status}
                                                </span>
                                            </div>
                                            {c.notes && (
                                                <div className="text-xs text-muted-clinical mt-2 line-clamp-2">{c.notes}</div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
            </div>

            {/* Goals checklist */}
            <div className="space-y-4">
                <div className="card-earthy">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-brand">
                            <Target size={18} /> <h2 className="font-display text-xl font-medium">Metas do acompanhamento</h2>
                        </div>
                        {goals.length > 0 && (
                            <span className="text-xs text-muted-clinical font-medium">
                                {achievedCount}/{goals.length} atingidas
                            </span>
                        )}
                    </div>
                    {goals.length === 0 ? (
                        <div className="text-sm text-muted-clinical py-6 text-center">
                            <AlertCircle size={22} className="mx-auto mb-2 opacity-60" />
                            Ainda não há metas SMART registradas para este paciente.
                            <div className="text-xs mt-2">Adicione no <strong>Editor estruturado</strong> do plano de cuidado.</div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {pendingGoals.length > 0 && (
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-brand-secondary font-medium mb-2">
                                        Pendentes ({pendingGoals.length})
                                    </div>
                                    <ul className="space-y-2">
                                        {pendingGoals.map((g) => {
                                            const idx = goals.indexOf(g);
                                            const pmeta = PRIORITY_META[g.problem_priority] || PRIORITY_META.media;
                                            const isOverdue = g.deadline && new Date(g.deadline).getTime() < Date.now();
                                            return (
                                                <li key={`${g.care_plan_id}-${g.problem_index}-${g.goal_index}`}
                                                    data-testid={`goal-${g.care_plan_id}-${g.problem_index}-${g.goal_index}`}
                                                    className="border border-[rgba(30,63,51,0.08)] rounded-xl p-3">
                                                    <div className="flex items-start gap-3">
                                                        <button
                                                            onClick={() => toggleGoal(g, idx, true)}
                                                            disabled={!canEdit || savingIdx === idx}
                                                            data-testid={`goal-toggle-${g.care_plan_id}-${g.problem_index}-${g.goal_index}`}
                                                            className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${canEdit ? "border-brand hover:bg-alt cursor-pointer" : "border-[rgba(30,63,51,0.2)] cursor-not-allowed"}`}
                                                            title={canEdit ? "Marcar como atingida" : "Somente médico/admin pode marcar"}
                                                        >
                                                            {savingIdx === idx && <Loader2 size={10} className="animate-spin text-brand" />}
                                                        </button>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-xs text-muted-clinical">{g.problem_title}</div>
                                                            <div className="text-sm text-brand font-medium">{g.title || "(sem descrição)"}</div>
                                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${pmeta.cls}`}>
                                                                    {pmeta.label}
                                                                </span>
                                                                {g.deadline && (
                                                                    <span className={`text-[11px] flex items-center gap-1 ${isOverdue ? "text-[var(--status-danger)] font-semibold" : "text-muted-clinical"}`}>
                                                                        <Clock size={10} /> {formatDate(g.deadline)}
                                                                        {isOverdue && " · atrasada"}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                            {achievedGoals.length > 0 && (
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-brand-secondary font-medium mb-2 mt-4">
                                        Atingidas ({achievedGoals.length})
                                    </div>
                                    <ul className="space-y-2">
                                        {achievedGoals.map((g) => {
                                            const idx = goals.indexOf(g);
                                            return (
                                                <li key={`${g.care_plan_id}-${g.problem_index}-${g.goal_index}`}
                                                    className="border border-[rgba(49,91,58,0.2)] bg-[#F1F6EE] rounded-xl p-3">
                                                    <div className="flex items-start gap-3">
                                                        <button
                                                            onClick={() => toggleGoal(g, idx, false)}
                                                            disabled={!canEdit || savingIdx === idx}
                                                            data-testid={`goal-toggle-${g.care_plan_id}-${g.problem_index}-${g.goal_index}`}
                                                            className={`mt-0.5 w-5 h-5 rounded-full bg-[#315B3A] text-white flex items-center justify-center shrink-0 ${canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
                                                            title={canEdit ? "Reabrir meta" : "Somente médico/admin pode alterar"}
                                                        >
                                                            {savingIdx === idx ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                                        </button>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-xs text-muted-clinical">{g.problem_title}</div>
                                                            <div className="text-sm text-[#315B3A] font-medium line-through decoration-1">
                                                                {g.title || "(sem descrição)"}
                                                            </div>
                                                            {g.achieved_at && (
                                                                <div className="text-[11px] text-muted-clinical mt-1">
                                                                    Concluída em {formatDate(g.achieved_at)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                            {!canEdit && (
                                <div className="text-[11px] text-muted-clinical border-t border-[rgba(30,63,51,0.08)] pt-3 flex items-start gap-2">
                                    <Circle size={10} className="mt-0.5" />
                                    <span>Somente médico/admin pode marcar metas como atingidas.</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
